/**
 * Commercial status (E4, spec D21 §8.2): the status derived from the
 * deployment pack's map, where it came from, and the owner's override —
 * bounded by the map's `overrides`. No map, no control: the platform has no
 * status vocabulary of its own.
 */
import { useState } from 'react'
import { Button, Input, NativeSelect } from '@/components/ui'
import { recordErrorMessage, useSetCommercialStatus } from '@/hooks/useRecordExtras'
import { RecordSection } from './RecordSection'
import { statusSummary } from './sectionSummaries'
import type { Order } from '@/types/commerce'

export function StatusOverrideControl({
  order, overrides, labels, focused = false, onChanged,
}: {
  order: Order
  overrides: string[]
  labels: Record<string, string>
  /** `?panel=status` (spec §7.5). */
  focused?: boolean
  /** The override landed: the page refetches the record. */
  onChanged: () => void
}) {
  const set = useSetCommercialStatus(order.id)
  const [choice, setChoice] = useState('')
  const [note, setNote] = useState('')
  const status = order.commercialStatus ?? null
  if (!status && overrides.length === 0) return null

  const apply = (override: string | null) => {
    set.mutate(
      { override, note: override ? note.trim() || null : null },
      { onSuccess: () => { setChoice(''); setNote(''); onChanged() } },
    )
  }

  return (
    <RecordSection id="status" title="Status" summary={statusSummary(status)} defaultOpen={false} focused={focused}>
      <p className="text-sm text-foreground">{statusSummary(status)}</p>
      {order.commercialStatusOverride && order.commercialStatusOverrideNote && (
        <p className="text-xs italic text-muted-foreground mt-1">“{order.commercialStatusOverrideNote}”</p>
      )}
      {overrides.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mt-3">
          <div className="w-52">
            <NativeSelect aria-label="Set status by hand" value={choice} onChange={e => setChoice(e.target.value)}>
              <option value="">Choose…</option>
              {overrides.map(key => <option key={key} value={key}>{labels[key] ?? key}</option>)}
            </NativeSelect>
          </div>
          <div className="flex-1 min-w-[10rem]">
            <Input aria-label="Note (optional)" placeholder="Why (optional)" value={note}
              onChange={e => setNote(e.target.value)} />
          </div>
          <Button variant="secondary" size="sm" disabled={set.isPending || !choice} onClick={() => apply(choice)}>
            Set status
          </Button>
        </div>
      )}
      {order.commercialStatusOverride && (
        <Button variant="ghost" size="sm" className="mt-2 text-muted-foreground" disabled={set.isPending}
          onClick={() => apply(null)}>
          Clear override
        </Button>
      )}
      {set.error && <p className="text-sm text-destructive mt-2">{recordErrorMessage(set.error)}</p>}
    </RecordSection>
  )
}
